import type { ReactNode } from 'react';

/**
 * One row of a tree that opens. The categories of a product list and the category tree itself
 * share it, so they look and behave the same. A row without `onToggle` has nothing under it.
 */
export function TreeRow({
  name,
  depth,
  open = false,
  onToggle,
  lead,
  aside,
  testId,
  children,
}: {
  name: string;
  depth: number;
  open?: boolean;
  /** Opens or closes what is under the row; undefined for a row with nothing under it. */
  onToggle?: (() => void) | undefined;
  /** Shown at the start of the row, outside the button (a checkbox). */
  lead?: ReactNode;
  /** Shown at the end of the row, outside the button (a count, badges, actions). */
  aside?: ReactNode;
  testId: string;
  children?: ReactNode;
}) {
  const tone = depth === 0 ? 'bg-slate-50 font-semibold text-slate-900' : 'bg-white text-slate-800';
  const label = (
    <>
      <svg
        aria-hidden="true"
        viewBox="0 0 20 20"
        fill="currentColor"
        className={`size-4 shrink-0 text-slate-500 transition-transform ${
          onToggle === undefined ? 'invisible' : open ? '' : 'rotate-90'
        }`}
      >
        <path d="M5.2 7.6a.75.75 0 0 1 1.06-.04L10 11.03l3.74-3.47a.75.75 0 1 1 1.02 1.1l-4.25 3.94a.75.75 0 0 1-1.02 0L5.24 8.66a.75.75 0 0 1-.04-1.06Z" />
      </svg>
      <span className="min-w-0 flex-1 break-words">{name}</span>
    </>
  );
  const labelClass = 'flex min-w-0 flex-1 items-center gap-2 py-2.5 text-start';
  const indent = { paddingInlineStart: `${String(0.75 + depth * 1.25)}rem` };
  return (
    <li
      data-testid={testId}
      data-category={name}
      data-open={String(open)}
      className="border-b border-slate-200 last:border-b-0"
    >
      <div
        style={indent}
        className={`flex flex-wrap items-center gap-2 pe-3 text-sm transition-colors hover:bg-brand-50 ${tone}`}
      >
        {lead}
        {onToggle === undefined ? (
          <div className={labelClass}>{label}</div>
        ) : (
          <button type="button" aria-expanded={open} onClick={onToggle} className={labelClass}>
            {label}
          </button>
        )}
        {aside}
      </div>
      {open && children !== undefined ? (
        <div className="border-t border-slate-200">{children}</div>
      ) : null}
    </li>
  );
}

/** The frame of a tree of rows. */
export function TreeList({
  label,
  testId,
  children,
}: {
  label: string;
  testId: string;
  children: ReactNode;
}) {
  return (
    <ul
      aria-label={label}
      data-testid={testId}
      className="flex flex-col overflow-hidden rounded-md ring-1 ring-slate-200"
    >
      {children}
    </ul>
  );
}
